import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { abbreviateSizeLabel } from '@theme/cw-size-label';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} [quantityDisplay] - Quantity display container
 * @property {HTMLElement} [quantityNumber] - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 * @property {HTMLButtonElement} buyNowButton - Sticky bar's buy now button
 * @property {HTMLButtonElement} closeButton - Dismiss sticky bar button
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {Element | null} */
  #productSection = null;

  /** @type {number} */
  #contentSpace = 0;

  /** @type {ResizeObserver | null} */
  #barResizeObserver = null;

  /** @type {boolean} */
  #dismissedByUser = false;

  connectedCallback() {
    super.connectedCallback();
    this.#productSection = this.closest('.shopify-section');

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.#productSection;
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#observeBarSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#barResizeObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    this.#clearContentSpace();
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#cacheTargetButtons(productForm);
  }

  #observeBarSize() {
    const { stickyBar } = this.refs;
    if (!stickyBar || this.#barResizeObserver) return;

    this.#barResizeObserver = new ResizeObserver(() => this.#syncContentSpace());
    this.#barResizeObserver.observe(stickyBar);
  }

  #syncContentSpace() {
    const { stickyBar } = this.refs;
    const space = this.#isStuck && stickyBar ? Math.ceil(stickyBar.offsetHeight) : 0;
    if (space === this.#contentSpace) return;
    this.#contentSpace = space;
    document.documentElement.style.setProperty('--sticky-atc-space', `${space}px`);
  }

  #clearContentSpace() {
    this.#contentSpace = 0;
    document.documentElement.style.removeProperty('--sticky-atc-space');
  }

  /**
   * @param {HTMLElement} productForm
   */
  /** @param {HTMLElement} productForm */
  #getCheckoutContainer(productForm) {
    return (
      productForm.querySelector('[ref="acceleratedCheckoutButtonContainer"]') ??
      productForm.querySelector('.accelerated-checkout-block')
    );
  }

  /** @param {HTMLElement} productForm */
  #syncFormVariantForBuyNow(productForm) {
    const input = productForm.querySelector('[ref="variantId"]');
    if (!(input instanceof HTMLInputElement)) return;

    const id = new URL(location.href).searchParams.get('variant') || this.dataset.currentVariantId;
    if (!id || input.value === String(id)) return;

    input.value = String(id);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {HTMLElement} productForm
   * @param {Element | null} [buyItNowHost]
   * @param {Element | null} [acceleratedHost]
   */
  #triggerNativeBuyNow(productForm, buyItNowHost, acceleratedHost) {
    const container = this.#getCheckoutContainer(productForm);
    if (!container || container.hasAttribute('hidden')) return;

    const checkout = productForm.querySelector('form[data-type="add-to-cart-form"] [name="checkout"]');
    if (checkout instanceof HTMLElement) {
      checkout.click();
      return;
    }

    buyItNowHost ??= container.querySelector('shopify-buy-it-now-button');
    acceleratedHost ??= container.querySelector('shopify-accelerated-checkout');

    for (const el of [
      container.querySelector('button.shopify-payment-button__button:not([disabled])'),
      buyItNowHost?.shadowRoot?.querySelector('button:not([disabled])'),
      acceleratedHost?.shadowRoot?.querySelector('button:not([disabled])'),
      buyItNowHost,
      acceleratedHost,
      container.querySelector('.shopify-payment-button button:not([disabled])'),
    ]) {
      if (el instanceof HTMLElement && (!(el instanceof HTMLButtonElement) || !el.disabled)) {
        el.click();
        return;
      }
    }
  }

  /** @param {HTMLElement} productForm */
  #cacheTargetButtons(productForm) {
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    const { buyNowButton } = this.refs;
    if (!buyNowButton) return;

    const container = this.#getCheckoutContainer(productForm);
    buyNowButton.disabled =
      this.dataset.variantAvailable !== 'true' || !container || container.hasAttribute('hidden');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) {
      const productForm = this.#getProductForm();
      if (productForm) this.#cacheTargetButtons(productForm);
    }
    if (!this.#targetAddToCartButton) return;

    const atcComponent = /** @type {HTMLElement | null} */ (
      this.#targetAddToCartButton.closest('add-to-cart-component')
    );

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    await this.#playAddToCartAnimation(atcComponent);
  };

  handleCloseClick = () => {
    this.#dismissedByUser = true;
    this.#hideStickyBar();
  };

  handleBuyNowClick = async () => {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;

    const buyItNowHost = productForm.querySelector('shopify-buy-it-now-button');
    const acceleratedHost = productForm.querySelector('shopify-accelerated-checkout');
    if (buyItNowHost) await customElements.whenDefined('shopify-buy-it-now-button');
    if (acceleratedHost) await customElements.whenDefined('shopify-accelerated-checkout');
    await yieldToMainThread();

    this.#syncFormVariantForBuyNow(productForm);
    if (!form.checkValidity()) return form.reportValidity();

    this.#triggerNativeBuyNow(productForm, buyItNowHost, acceleratedHost);
  };

  /**
   * Matches main PDP add-to-cart-component animation on the sticky button.
   * @param {HTMLElement | null} atcComponent
   */
  #playAddToCartAnimation = async (atcComponent) => {
    const { addToCartButton } = this.refs;

    if (atcComponent?.dataset.addToCartAnimation === 'true') {
      const cartIcon = document.querySelector('.header-actions__cart-icon');
      const image = atcComponent.dataset.productVariantMedia;

      if (cartIcon && image) {
        const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
        flyToCartElement.classList.add('fly-to-cart--main');
        flyToCartElement.style.setProperty('background-image', `url(${image})`);
        flyToCartElement.style.setProperty('--start-opacity', '0');
        flyToCartElement.source = addToCartButton;
        flyToCartElement.destination = cartIcon;
        document.body.appendChild(flyToCartElement);
      }
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    if (addToCartButton.dataset.added !== 'true') addToCartButton.dataset.added = 'true';

    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#cacheTargetButtons(productForm);
    } else if (this.refs.buyNowButton) {
      this.refs.buyNowButton.disabled = true;
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('fieldset input:checked'))
      .map((input) => {
        const el = /** @type {HTMLInputElement} */ (input);
        const raw = el.value;
        if (!raw) return '';
        const fieldset = el.closest('fieldset');
        if (fieldset?.classList.contains('variant-option--size')) {
          return abbreviateSizeLabel(raw);
        }
        return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
      })
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    if (this.#dismissedByUser) return;

    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#syncContentSpace();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    this.#animationTimeout = setTimeout(() => {
      this.#animationTimeout = null;
      if (!this.#isStuck) this.#syncContentSpace();
    }, 320);
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.#productSection ?? this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} .buy-buttons-block product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    if (!quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();
    quantityDisplay.style.display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
